/**
 * NavigationStateContext
 *
 * Broadcasts the current react-navigation state to components that live
 * outside the NavigationContainer (i.e., the DesktopSidebar).
 *
 * Usage:
 *   1. Wrap your app with <NavigationStateProvider>.
 *   2. Pass onStateChange={setNavState} to <NavigationContainer>.
 *   3. Components outside the navigator call useNavState() to read state
 *      and navigationRef.navigate() to trigger navigation.
 */

import React, { createContext, useContext, useState } from 'react';
import type { NavigationState, PartialState } from '@react-navigation/native';

type AnyNavState = NavigationState | PartialState<NavigationState> | undefined;

interface NavigationStateContextValue {
  navState: AnyNavState;
  setNavState: (state: AnyNavState) => void;
}

const NavigationStateContext = createContext<NavigationStateContextValue>({
  navState: undefined,
  setNavState: () => {},
});

export function NavigationStateProvider({ children }: { children: React.ReactNode }) {
  const [navState, setNavState] = useState<AnyNavState>(undefined);

  return (
    <NavigationStateContext.Provider value={{ navState, setNavState }}>
      {children}
    </NavigationStateContext.Provider>
  );
}

export function useNavState(): AnyNavState {
  return useContext(NavigationStateContext).navState;
}

export function useSetNavState(): (state: AnyNavState) => void {
  return useContext(NavigationStateContext).setNavState;
}

// ── Navigation state helpers ──────────────────────────────────────────────────

interface ActiveRouteInfo {
  isLoggedIn: boolean;
  rootScreen: string | null;     // e.g. 'Main', 'Login', 'Onboarding'
  activeTab: string | null;      // e.g. 'AI', 'Plans', 'Settings'
  activePlansScreen: string | null; // e.g. 'ProjectChat', 'ProjectFiles'
  projectId: string | number | null;
  project: Record<string, unknown> | null;
  projectName: string;
  activePlansParams: Record<string, unknown> | null;
}

/**
 * Derives useful routing info from the raw NavigationState tree.
 * Handles partial states (during animation) gracefully.
 */
export function getActiveRouteInfo(state: AnyNavState): ActiveRouteInfo {
  const empty: ActiveRouteInfo = {
    isLoggedIn: false, rootScreen: null, activeTab: null,
    activePlansScreen: null, projectId: null, project: null,
    projectName: '', activePlansParams: null,
  };

  if (!state) return empty;

  const routes = (state as NavigationState).routes;
  if (!routes?.length) return empty;

  const idx = (state as NavigationState).index ?? routes.length - 1;
  const rootRoute = routes[idx];
  if (!rootRoute) return empty;

  const rootScreen = rootRoute.name;
  const isLoggedIn = rootScreen === 'Main';

  if (!isLoggedIn) return { ...empty, rootScreen, isLoggedIn: false };

  // Descend into the tab navigator state
  const mainState = (rootRoute as any).state as AnyNavState;
  if (!mainState) return { ...empty, rootScreen, isLoggedIn };

  const mainRoutes = (mainState as NavigationState).routes ?? [];
  const mainIdx    = (mainState as NavigationState).index ?? mainRoutes.length - 1;
  const activeTab  = mainRoutes[mainIdx]?.name ?? null;

  // Descend into Plans stack if that tab is active
  const plansRoute  = mainRoutes.find((r) => r.name === 'Plans');
  const plansState  = (plansRoute as any)?.state as AnyNavState;
  const plansRoutes = plansState ? (plansState as NavigationState).routes ?? [] : [];
  const plansIdx    = plansState ? ((plansState as NavigationState).index ?? plansRoutes.length - 1) : -1;
  const plansTop    = plansIdx >= 0 ? plansRoutes[plansIdx] : null;

  const activePlansScreen = plansTop?.name ?? null;
  const activePlansParams = (plansTop?.params as Record<string, unknown>) ?? null;

  const projectId =
    activePlansParams?.projectId ??
    (activePlansParams?.project as any)?.id ??
    (activePlansParams?.project as any)?.project_id ??
    null;

  const project = (activePlansParams?.project as Record<string, unknown>) ?? null;
  const projectName = (project?.project_name ?? project?.first_name ?? '') as string;

  return {
    isLoggedIn, rootScreen, activeTab,
    activePlansScreen, projectId, project, projectName, activePlansParams,
  };
}
